import Application from "./Application";
import common from "./common";
import PreloaderScene from "./scenes/PreloaderScene";

export default class Main {
    /**
     * @param {number} [width=1024]
     * @param {number} [height=768]
     */
    constructor(width = 1024, height = 768) {
        this._width = width;
        this._height = height;
        this._app = null;
        this._preloader = null;
    }

    init() {
        P3.assets.audioFileTypes = ["ogg", "mp3"];

        PIXI.settings.RETINA_PREFIX = /\_(?=[^_]*$)(.+)x/;
        PIXI.settings.MIPMAP_TEXTURES = false;


        typeof TweenMax !== "undefined" && (TweenMax.defaultOverwrite = "none");

        const options = {
            width: this._width,
            height: this._height,
            holderId: "game",
            portrait: false,
            rotateImageUrl: "./assets/images/localised/rotate_screen_en.jpg",
            rotateImageColor: "#000000",
            aspectRatioFillHeight: true
        };

        const view = new P3.View(options);
        view.signals.ready.once(function (canvas) {
            const options = {};
            options.view = canvas;
            options.transparent = false;
            options.antialias = false;
            options.preserveDrawingBuffer = false;
            options.resolution = 1;

            const stage = new PIXI.Container();
            common.stage = stage;

            P3.input.init(stage, canvas);

            if (P3.utils.isCocoonJS()) {
                stage.scale.x = window.innerHeight / options.height;
                stage.scale.y = window.innerHeight / options.height;
            }

            const renderer = !P3.utils.isCocoonJS() ?
                PIXI.autoDetectRenderer(this._width, this._height, options) :
                new PIXI.WebGLRenderer(window.innerWidth, window.innerHeight, options);
            renderer.backgroundColor = 0x000000;
            common.renderer = renderer;

            common.stage.addChild(P3.scene.view);

            const transitions = new PIXI.Container();
            common.stage.addChild(transitions);
            common.transitions = transitions;

            const ticker = new P3.Ticker();
            ticker.init(function () {
                P3.scene.update();
                P3.input.update();
                P3.utils.TWEEN.update(); // comment this line out if you are using TweenMax
            }, function () {
                renderer.render(stage);
            }, this);
            common.ticker = ticker;

            this.loadPreAssets();
        }, this);
        view.signals.resize.add(this.onCanvasResize, this);
        common.view = view;

        this.loadConfig(function () {
            view.options.rotateImageUrl = "./assets/images/localised/rotate_screen_" + common.config.language + ".jpg" || "./assets/images/localised/rotate_screen_en.jpg";
            view.init()
        }, this); // we load the config before we do anything
    }

    loadConfig(callback, scope) {
        scope = scope || window;
        P3.assets.addFiles([{
            name: "config",
            url: "config" + ".json"
        }], "./");
        P3.assets.signals.complete.once(function () {
            common.config = P3.assets.json("config");
            common.config.mobile = PIXI.utils.isMobile.any;
            callback && callback.call(scope);
        }, this);
        P3.assets.load();
    }

    loadPreAssets() {
        const files = [
            {name: "preloader_1x", url: "images/preloader_1x" + ".json"},
            {name: "bg_preloader_pot", url: "images/bg_preloader_pot" + ".jpg"},
            {name: "vag_60", url: "fonts/" + "vag_60" + ".xml"},
            {name: "vag_120", url: "fonts/" + "vag_120" + ".xml"}
        ];
        if (files.length) {
            P3.assets.addFiles(files, "./assets/");
            P3.assets.signals.complete.once(this.loadAssets, this);
            P3.assets.load();
        } else this.loadAssets();
    }

    loadAssets() {
        let particle_bowl_flour_burst_00 = "particle_bowl_flour_burst_00";
        let particle_bowl_flour_mixing_00 = "particle_bowl_flour_mixing_00";
        let particle_over_steam_loop_00 = "particle_over_steam_loop_00";
        let particle_rolling_pin_00 = "particle_rolling_pin_00";
        let particle_top_flour_burst_00 = "particle_top_flour_burst_00";
        let particle_final_cookie_loop_00 = "particle_final_cookie_loop_00";
        let particle_oven_tray_complete_00 = "particle_oven_tray_complete_00";
        let particle_bowl_flavours_burst_00 = "particle_bowl_flavours_burst_00";
        let particle_ing_flour = "particle_ing_flour";
        let particle_ing_rasin = "particle_ing_rasin";
        let particle_ing_strawberry = "particle_ing_strawberry";
        let particle_ing_sugar = "particle_ing_sugar";
        let particle_ing_egg = "particle_ing_egg";
        let particle_ing_butter = "particle_ing_butter";
        let particle_ing_choc_dark = "particle_ing_choc_dark";
        let particle_ing_choc_lemon = "particle_ing_choc_lemon";
        let particle_ing_choc_orange = "particle_ing_choc_orange";
        let particle_ing_choc_white = "particle_ing_choc_white";
        let particle_love_hearts_00 = "particle_love_hearts_00";



        const files = [
            //RECIPES
            {name: "RecipeData",url: "data/" + "RecipeData" + ".json"},

            //UI
            {name: "title", url: "images/localised/title_" + common.config.language + ".png"},
            {name: "ui_1x", url: "images/ui_1x" + ".json"},

            {name: "buttons_1x", url: "images/buttons_1x" + ".json"},

            //BACKGROUNDS
            {name: "splash_1x", url: "images/splash_1x" + ".json"},

            {name: "bg_kitchen", url: "images/bg_kitchen_pot" + ".jpg"},
            {name: "bg_oven", url: "images/bg_oven_pot" + ".jpg"},
            {name: "bg_table", url: "images/bg_table_pot" + ".jpg"},
            {name: "bg_intro", url: "images/bg_intro_pot" + ".jpg"},
            {name: "bg_splash", url: "images/bg_splash_pot" + ".jpg"},
            {name: "bg_decorate", url: "images/bg_decorate_pot" + ".jpg"},
            {name: "bg_photo", url: "images/bg_photo_pot" + ".jpg"},

            //GAME ART
            {name: "game_assets_0_1x", url: "images/game_assets_0_1x" + ".json"},
            {name: "game_assets_1_1x", url: "images/game_assets_1_1x" + ".json"},
            {name: "game_assets_2_1x", url: "images/game_assets_2_1x" + ".json"},


            //PARTICLES
            {name: `${particle_bowl_flour_burst_00}`, url: `particles/${particle_bowl_flour_burst_00}.json`},
            {name: `${particle_bowl_flour_mixing_00}`, url: `particles/${particle_bowl_flour_mixing_00}.json`},
            {name: `${particle_over_steam_loop_00}`, url: `particles/${particle_over_steam_loop_00}.json`},
            {name: `${particle_rolling_pin_00}`, url: `particles/${particle_rolling_pin_00}.json`},
            {name: `${particle_top_flour_burst_00}`, url: `particles/${particle_top_flour_burst_00}.json`},
            {name: `${particle_final_cookie_loop_00}`, url: `particles/${particle_final_cookie_loop_00}.json`},
            {name: `${particle_oven_tray_complete_00}`, url: `particles/${particle_oven_tray_complete_00}.json`},
            {name: `${particle_bowl_flavours_burst_00}`, url: `particles/${particle_bowl_flavours_burst_00}.json`},
            {name: `${particle_ing_flour}`, url: `particles/${particle_ing_flour}.json`},
            {name: `${particle_ing_rasin}`, url: `particles/${particle_ing_rasin}.json`},
            {name: `${particle_ing_strawberry}`, url: `particles/${particle_ing_strawberry}.json`},
            {name: `${particle_ing_sugar}`, url: `particles/${particle_ing_sugar}.json`},
            {name: `${particle_ing_egg}`, url: `particles/${particle_ing_egg}.json`},
            {name: `${particle_ing_butter}`, url: `particles/${particle_ing_butter}.json`},
            {name: `${particle_ing_choc_dark}`, url: `particles/${particle_ing_choc_dark}.json`},
            {name: `${particle_ing_choc_lemon}`, url: `particles/${particle_ing_choc_lemon}.json`},
            {name: `${particle_ing_choc_orange}`, url: `particles/${particle_ing_choc_orange}.json`},
            {name: `${particle_ing_choc_white}`, url: `particles/${particle_ing_choc_white}.json`},
            {name: `${particle_love_hearts_00}`, url: `particles/${particle_love_hearts_00}.json`},

            //ANIMATION
            {name: "dorothy", url: "spine/dorothy" + ".json"},
            {name: "dough", url: "spine/dough" + ".json"},
            {name: "oven", url: "spine/oven" + ".json"},
            {name: "timer", url: "spine/timer" + ".json"},

            //AUDIO
                //loops
                {name: "music_ambient_music_loop_00", url: "audio/" + "music_ambient_music_loop_00" + P3.assets.audioFileExt},  //DONE
                {name: "music_cookie_complete_00", url: "audio/" + "music_cookie_complete_00" + P3.assets.audioFileExt},
                {name: "music_final_photo_complete_screen_00", url: "audio/" + "music_final_photo_complete_screen_00" + P3.assets.audioFileExt},    //DONE
                {name: "music_pausehelp_menu_00", url: "audio/" + "music_pausehelp_menu_00" + P3.assets.audioFileExt},  //DONE
                {name: "music_dorothy_complete_00", url: "audio/" + "music_dorothy_complete_00" + P3.assets.audioFileExt},
                {name: "music_restart_00", url: "audio/" + "music_restart_00" + P3.assets.audioFileExt},
                {name: "sfx_final_cookie_loop_00", url: "audio/" + "sfx_final_cookie_loop_00" + P3.assets.audioFileExt},

                //sfx
                {name: "sfx_add_ingredient_00", url: "audio/" + "sfx_add_ingredient_00" + P3.assets.audioFileExt},
                {name: "sfx_add_ingredient_01", url: "audio/" + "sfx_add_ingredient_00" + P3.assets.audioFileExt},  //DONE
                {name: "sfx_add_ingredient_02", url: "audio/" + "sfx_add_ingredient_00" + P3.assets.audioFileExt},

                {name: "sfx_add_splodge_00", url: "audio/" + "sfx_add_splodge_00" + P3.assets.audioFileExt},

                {name: "sfx_add_sprinkle_00", url: "audio/" + "sfx_add_sprinkle_00" + P3.assets.audioFileExt}
                ,
                {name: "sfx_add_sprinkles_00", url: "audio/" + "sfx_add_sprinkles_00" + P3.assets.audioFileExt},
                {name: "sfx_add_sprinkles_01", url: "audio/" + "sfx_add_sprinkles_01" + P3.assets.audioFileExt},    //DONE
                {name: "sfx_add_sprinkles_02", url: "audio/" + "sfx_add_sprinkles_02" + P3.assets.audioFileExt},


                {name: "sfx_take_final_photo", url: "audio/" + "sfx_take_final_photo" + P3.assets.audioFileExt},

                {name: "sfx_btn_change_camera_pose_00", url: "audio/" + "sfx_btn_change_camera_pose_00" + P3.assets.audioFileExt},  //DONE

                {name: "sfx_character_request_00", url: "audio/" + "sfx_character_request_00" + P3.assets.audioFileExt},    //DONE

                {name: "sfx_choose_cutter_00", url: "audio/" + "sfx_choose_cutter_00" + P3.assets.audioFileExt},    //DONE

                {name: "sfx_cookie_finished_00", url: "audio/" + "sfx_cookie_finished_00" + P3.assets.audioFileExt},

                {name: "sfx_dorothy_jump_in_00", url: "audio/" + "sfx_dorothy_jump_in_00" + P3.assets.audioFileExt},    //DONE

                {name: "sfx_mix_loop_00", url: "audio/" + "sfx_mix_loop_00" + P3.assets.audioFileExt},


                {name: "sfx_open_oven_00", url: "audio/" + "sfx_open_oven_00" + P3.assets.audioFileExt},    //DONE
                {name: "sfx_close_oven_00", url: "audio/" + "sfx_close_oven_00" + P3.assets.audioFileExt},  //DONE

                {name: "sfx_pickup_ingredient_00", url: "audio/" + "sfx_pickup_ingredient_00" + P3.assets.audioFileExt},
                {name: "sfx_pickup_ingredient_01", url: "audio/" + "sfx_pickup_ingredient_01" + P3.assets.audioFileExt},
                {name: "sfx_pickup_ingredient_02", url: "audio/" + "sfx_pickup_ingredient_02" + P3.assets.audioFileExt},    //DONE
                {name: "sfx_pickup_ingredient_03", url: "audio/" + "sfx_pickup_ingredient_03" + P3.assets.audioFileExt},
                {name: "sfx_pickup_ingredient_04", url: "audio/" + "sfx_pickup_ingredient_04" + P3.assets.audioFileExt},

                {name: "sfx_press_cutter_00", url: "audio/" + "sfx_press_cutter_00" + P3.assets.audioFileExt},  //DONE

                {name: "sfx_roll_dough_00", url: "audio/" + "sfx_roll_dough_00" + P3.assets.audioFileExt},  //DONE
                {name: "sfx_roll_dough_01", url: "audio/" + "sfx_roll_dough_01" + P3.assets.audioFileExt},

                {name: "sfx_stage_complete_00", url: "audio/" + "sfx_stage_complete_00" + P3.assets.audioFileExt},  //DONE
                {name: "sfx_next_stage_00", url: "audio/" + "sfx_next_stage_00" + P3.assets.audioFileExt},

                {name: "sfx_whoosh_00", url: "audio/" + "sfx_whoosh_00" + P3.assets.audioFileExt},

                {name: "sfx_btn_accept_00", url: "audio/" + "sfx_btn_accept_00" + P3.assets.audioFileExt},  //DONE

                {name: "sfx_oven_timer_ping_00", url: "audio/" + "sfx_oven_timer_ping_00" + P3.assets.audioFileExt},  //DONE
                {name: "sfx_over_timer_loop_00", url: "audio/" + "sfx_over_timer_loop_00" + P3.assets.audioFileExt},  //DONE


        ];

        if (files.length) {
            this._preloader = new PreloaderScene();
            P3.scene.show(this._preloader);

            P3.assets.addFiles(files, "./assets/");
            P3.assets.signals.progress.add(this.onLoadingProgress, this);
            P3.assets.signals.complete.once(this.onLoadingCompleted, this);
            P3.assets.load();

        } else this.start();
    }

    start() {
        this._app = new Application();
        this._app.init();
    }

    onLoadingProgress(event) {
        this._preloader && (this._preloader.loaded = event.progress / 100);
        //console.log("loading:", event.progress);
    }

    onLoadingCompleted() {
        this._preloader && (this._preloader.loaded = 1);
        //this._preloader.signals.next.add(function() {
        this.start();
        //}, this);
    }

    onCanvasResize(correct) {
        if (correct) {
            common.renderer.resize(P3.View.width, P3.View.height);
            if (P3.scene) {
                P3.scene.resize();

                for (let i = 0; i < common.transitions.children.length; ++i) {
                    const transition = common.transitions.children[i];
                    transition.resize && transition.resize();
                }
            }
        }
    }
}